// frontend/modules/customer-category/components/onboarding/ProductsStep.tsx
import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Typography,
    Tag,
    Space,
    Tooltip,
    Modal,
    Select,
    Alert,
    Checkbox
} from 'antd';
import {
    PlusOutlined,
    DeleteOutlined,
    ExclamationCircleOutlined,
    InfoCircleOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import {
    CustomerCategoryDTO,
    CustomerOnboardingData,
    ProductDTO
} from '../../types';
import { useProducts } from '../../hooks/useProducts';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;
const { confirm } = Modal;

interface ProductsStepProps {
    category: CustomerCategoryDTO | null;
    formData: CustomerOnboardingData;
    onUpdateFormData: (data: Partial<CustomerOnboardingData>) => void;
}

const ProductsStep: React.FC<ProductsStepProps> = ({
                                                       category,
                                                       formData,
                                                       onUpdateFormData
                                                   }) => {
    const { dark } = useTheme();
    const [selectedProducts, setSelectedProducts] = useState<ProductDTO[]>(
        formData.selectedProducts || []
    );

    const {
        allProducts,
        isLoading,
        fetchAllProducts,
        checkAccountBalance
    } = useProducts();

    // When component loads, fetch products if needed
    useEffect(() => {
        if (allProducts.length === 0) {
            fetchAllProducts();
        }
    }, [allProducts.length, fetchAllProducts]);

    // When category changes, or on initial load, apply default products
    useEffect(() => {
        if (category && !formData.isFreestyle && selectedProducts.length === 0) {
            // Get default products based on category
            const defaultProducts = (category.products || [])
                .filter(p => p.isDefault)
                .map(p => {
                    const product = allProducts.find(ap => ap.glNo === p.glNo);
                    if (product) {
                        return product;
                    }

                    // If product not found in allProducts, create a placeholder
                    return {
                        glNo: p.glNo,
                        productName: p.productName,
                        productType: p.productType,
                        description: `${p.productName} (${p.productType})`
                    };
                })
                .filter(p => p) as ProductDTO[];

            setSelectedProducts(defaultProducts);
            onUpdateFormData({ selectedProducts: defaultProducts });
        }
    }, [category, formData.isFreestyle, selectedProducts.length, allProducts, onUpdateFormData]);

    // When selectedProducts changes, update parent
    useEffect(() => {
        onUpdateFormData({ selectedProducts });
    }, [selectedProducts, onUpdateFormData]);

    const handleAddProduct = () => {
        // Filter out products already selected
        const availableProducts = allProducts.filter(
            p => !selectedProducts.some(sp => sp.glNo === p.glNo)
        );

        Modal.info({
            title: 'Add Product',
            content: (
                <div className="mt-4">
                    <Select
                        placeholder="Select a product to add"
                        style={{ width: '100%' }}
                        onChange={(value) => {
                            const product = allProducts.find(p => p.glNo === value);
                            if (product) {
                                setSelectedProducts([...selectedProducts, product]);
                                Modal.destroyAll();
                            }
                        }}
                    >
                        {availableProducts.map(product => (
                            <Option key={product.glNo} value={product.glNo}>
                                <Space>
                                    <span>{product.productName || product.acName || product.glNo}</span>
                                    <Tag color={
                                        product.productType === 'S' ? 'blue' :
                                            product.productType === 'H' ? 'green' :
                                                product.productType === 'F' ? 'orange' :
                                                    product.productType === 'L' ? 'red' : 'default'
                                    }>
                                        {mapProductType(product.productType || '')}
                                    </Tag>
                                </Space>
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            okText: 'Cancel',
            className: dark ? 'dark' : ''
        });
    };

    const handleRemoveProduct = (product: ProductDTO) => {
        // Check if product is required by category
        const isRequired = (category?.products || []).some(
            p => p.glNo === product.glNo && p.isRequired
        );

        if (isRequired) {
            Modal.error({
                title: 'Cannot Remove Required Product',
                content: `${product.productName || product.acName || product.glNo} is a required product for this customer category and cannot be removed.`,
                className: dark ? 'dark' : ''
            });
            return;
        }

        // Check if account has balance (would be implemented for existing customers in edit mode)
        // For new customers, this wouldn't apply

        confirm({
            title: 'Remove Product',
            icon: <ExclamationCircleOutlined />,
            content: `Are you sure you want to remove ${product.productName || product.acName || product.glNo}?`,
            onOk() {
                setSelectedProducts(selectedProducts.filter(p => p.glNo !== product.glNo));
            },
            className: dark ? 'dark' : ''
        });
    };

    // Helper to map product type codes to readable names
    const mapProductType = (type: string): string => {
        switch (type) {
            case 'S': return 'Savings';
            case 'H': return 'Shares';
            case 'F': return 'Fixed Deposit';
            case 'L': return 'Loan';
            default: return type;
        }
    };

    // Check if the required products are selected
    const areRequiredProductsSelected = () => {
        if (!category || formData.isFreestyle) return true;

        const requiredProducts = (category.products || []).filter(p => p.isRequired);

        return requiredProducts.every(required =>
            selectedProducts.some(p => p.glNo === required.glNo)
        );
    };

    // Get missing required products
    const getMissingRequiredProducts = () => {
        if (!category || formData.isFreestyle) return [];

        const requiredProducts = (category.products || []).filter(p => p.isRequired);

        return requiredProducts.filter(required =>
            !selectedProducts.some(p => p.glNo === required.glNo)
        );
    };

    const columns = [
        {
            title: 'Product Name',
            dataIndex: 'productName',
            key: 'name',
            render: (text: string, record: ProductDTO) => (
                <Space>
                    <span>{text || record.acName || record.glNo}</span>
                    {(category?.products || []).some(p => p.glNo === record.glNo && p.isRequired) && (
                        <Tooltip title="This product is required">
                            <InfoCircleOutlined className="text-blue-500" />
                        </Tooltip>
                    )}
                </Space>
            )
        },
        {
            title: 'Type',
            dataIndex: 'productType',
            key: 'type',
            render: (type: string) => (
                <Tag color={
                    type === 'S' ? 'blue' :
                        type === 'H' ? 'green' :
                            type === 'F' ? 'orange' :
                                type === 'L' ? 'red' : 'default'
                }>
                    {mapProductType(type)}
                </Tag>
            )
        },
        {
            title: 'GL Number',
            dataIndex: 'glNo',
            key: 'glNo'
        },
        {
            title: 'Action',
            key: 'action',
            render: (_:any, record: ProductDTO) => (
                <Button
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveProduct(record)}
                    size="small"
                    disabled={(category?.products || []).some(
                        p => p.glNo === record.glNo && p.isRequired
                    )}
                />
            )
        }
    ];

    const missingRequired = getMissingRequiredProducts();

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <Title level={5} className={dark ? 'text-white' : ''}>
                    Customer Products
                </Title>

                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={handleAddProduct}
                >
                    Add Product
                </Button>
            </div>

            {missingRequired.length > 0 && (
                <Alert
                    message="Missing Required Products"
                    description={
                        <div>
                            <Paragraph>
                                The following required products are missing:
                            </Paragraph>
                            <ul>
                                {missingRequired.map(p => (
                                    <li key={p.glNo}>{p.productName}</li>
                                ))}
                            </ul>
                        </div>
                    }
                    type="warning"
                    showIcon
                    className="mb-4"
                />
            )}

            <Table
                dataSource={selectedProducts}
                columns={columns}
                rowKey="glNo"
                pagination={false}
                loading={isLoading}
                locale={{ emptyText: 'No products selected' }}
            />

            {formData.isFreestyle && selectedProducts.length === 0 && (
                <Alert
                    message="Product Selection Required"
                    description="You need to select at least one product for this customer."
                    type="info"
                    showIcon
                    className="mt-4"
                />
            )}
        </div>
    );
};

export default ProductsStep;